/**
 * Settings page — API key management for supported Thread API access.
 *
 * Users can generate, view, and revoke API keys from this page.
 * Generated keys are shown once and cannot be retrieved again.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Dialog,
  Flex,
  Heading,

  Table,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";

import {
  fetchApiKeys,
  createApiKey,
  revokeApiKey,
  type ApiKey,
  type CreatedApiKey,
} from "../api/apiKeys";

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  if (s < 604800) return `${Math.floor(s / 86400)} days ago`;
  return `${Math.floor(s / 604800)} weeks ago`;
}

export default function Settings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: keys, isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: fetchApiKeys,
  });

  const [newKeyName, setNewKeyName] = useState("Default");
  const [flow360Key, setFlow360Key] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createApiKey({
        name: newKeyName,
        flow360_api_key: flow360Key || undefined,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setShowCreateDialog(false);
      setCreatedKey(data);
      setNewKeyName("Default");
      setFlow360Key("");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setRevokeTarget(null);
    },
  });

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box p="5" style={{ maxWidth: 800, margin: "0 auto" }}>
      <Flex justify="between" align="center" mb="2">
        <Button
          variant="ghost"
          size="1"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          &larr; Back
        </Button>
      </Flex>

      <Heading size="6" mb="1">
        Settings
      </Heading>
      <Text color="gray" size="2" mb="5" as="p">
        Manage bearer keys for supported Thread API clients.
      </Text>

      {/* API Keys Section */}
      <Card mb="5">
        <Flex justify="between" align="center" mb="4">
          <div>
            <Heading size="4">API Keys</Heading>
            <Text color="gray" size="2" as="p">
              Keys authenticate supported Thread API requests. The repository
              Python SDK currently supports local capture examples; it does not
              expose a general native V3 cloud writer.
            </Text>
          </div>
          <Dialog.Root
            open={showCreateDialog}
            onOpenChange={setShowCreateDialog}
          >
            <Dialog.Trigger>
              <Button size="2">Generate new key</Button>
            </Dialog.Trigger>
            <Dialog.Content maxWidth="480px">
              <Dialog.Title>Generate API Key</Dialog.Title>
              <Dialog.Description size="2" color="gray" mb="4">
                Give this key a name to identify it later.
              </Dialog.Description>

              <Flex direction="column" gap="3">
                <label>
                  <Text size="2" weight="medium" mb="1" as="p">
                    Key name
                  </Text>
                  <TextField.Root
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g. Laptop, CI Server"
                  />
                </label>
                <label>
                  <Text size="2" weight="medium" mb="1" as="p">
                    Flow360 API Key{" "}
                    <Text color="gray" size="1">
                      (optional)
                    </Text>
                  </Text>
                  <TextField.Root
                    value={flow360Key}
                    onChange={(e) => setFlow360Key(e.target.value)}
                    placeholder="SimCloud API key for Flow360 features"
                  />
                </label>
              </Flex>

              <Flex gap="3" mt="4" justify="end">
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  onClick={() => createMutation.mutate()}
                  disabled={!newKeyName.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? "Generating..." : "Generate"}
                </Button>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>
        </Flex>

        {isLoading ? (
          <Text color="gray" size="2">
            Loading keys...
          </Text>
        ) : !keys?.length ? (
          <Text color="gray" size="2">
            No API keys yet. Generate one for an authenticated API client.
          </Text>
        ) : (
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Key</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Last used</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Flow360</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {keys.map((key) => (
                <Table.Row key={key.id}>
                  <Table.Cell>
                    <Text weight="medium">{key.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Code size="2" color="gray">
                      {key.key_prefix}...
                    </Code>
                  </Table.Cell>
                  <Table.Cell>
                    <Tooltip content={new Date(key.created_at).toLocaleString()}>
                      <Text size="2" color="gray">
                        {timeAgo(key.created_at)}
                      </Text>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">
                      {key.last_used_at ? timeAgo(key.last_used_at) : "Never"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {key.has_flow360_key ? (
                      <Badge color="green" size="1">
                        Linked
                      </Badge>
                    ) : (
                      <Badge color="gray" size="1" variant="soft">
                        None
                      </Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="ghost"
                      color="red"
                      size="1"
                      onClick={() => setRevokeTarget(key)}
                      style={{ cursor: "pointer" }}
                    >
                      Revoke
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Card>

      {/* API Quick Start */}
      <Card>
        <Heading size="4" mb="2">
          Quick Start
        </Heading>
        <Text size="2" color="gray" as="p" mb="3">
          Copy the generated value once, keep it out of source control, and use
          it as a bearer credential with a route from the runtime OpenAPI document:
        </Text>
        <Code
          size="2"
          variant="soft"
          style={{
            display: "block",
            padding: "12px 16px",
            whiteSpace: "pre",
            lineHeight: 1.6,
          }}
        >
          {`export THREAD_API_ORIGIN=https://thread-api-preprod.simulation.cloud\nexport THREAD_API_KEY=thr_replace_with_your_key\ncurl -fsS -H "Authorization: Bearer $THREAD_API_KEY" "$THREAD_API_ORIGIN/api/projects"`}
        </Code>
      </Card>

      {/* Created Key Dialog — shows raw key once */}
      <Dialog.Root
        open={!!createdKey}
        onOpenChange={(open) => {
          if (!open) setCreatedKey(null);
        }}
      >
        <Dialog.Content maxWidth="520px">
          <Dialog.Title>API Key Created</Dialog.Title>
          <Dialog.Description size="2" color="red" mb="4">
            Copy this key now. You will not be able to see it again.
          </Dialog.Description>

          <Flex
            align="center"
            gap="2"
            p="3"
            style={{
              background: "var(--gray-a2)",
              borderRadius: "var(--radius-2)",
            }}
          >
            <Code
              size="2"
              style={{
                flex: 1,
                wordBreak: "break-all",
                userSelect: "all",
              }}
            >
              {createdKey?.raw_key}
            </Code>
            <Button
              variant="soft"
              size="1"
              onClick={() =>
                createdKey && copyToClipboard(createdKey.raw_key)
              }
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </Flex>

          <Flex justify="end" mt="4">
            <Dialog.Close>
              <Button>Done</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Revoke Confirmation */}
      <AlertDialog.Root
        open={!!revokeTarget}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
      >
        <AlertDialog.Content maxWidth="400px">
          <AlertDialog.Title>Revoke API Key</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Revoke <strong>{revokeTarget?.name}</strong> (
            <Code size="1">{revokeTarget?.key_prefix}...</Code>)? Any API
            clients using this key will immediately stop working.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                color="red"
                onClick={() =>
                  revokeTarget && revokeMutation.mutate(revokeTarget.id)
                }
              >
                Revoke
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Box>
  );
}
